import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothWiseInstagramUser = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { appId, language } = useAppId();
    const lang = language || 'en';

    const boothNo = location.state?.booth_no;

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: `Instagram Users - Booth ${boothNo || ''}`,
            searchPlaceholder: "Search by Name or ID...",
            noData: "No Instagram users found for this booth.",
            loadingData: "Fetching users...",
            idCardNo: "ID Card No",
            age: "Age",
        },
        hi: {
            pageTitle: `इंस्टाग्राम उपयोगकर्ता - बूथ ${boothNo || ''}`,
            searchPlaceholder: "नाम या वोटर ID से खोजें...",
            noData: "इस बूथ के लिए कोई इंस्टाग्राम उपयोगकर्ता नहीं मिला।",
            loadingData: "उपयोगकर्ता ला रहा है...",
            idCardNo: "ID Card No",
            age: "Age",
        },  
        gu: {
            pageTitle: `ઇન્સ્ટાગ્રામ યુઝર્સ - બૂથ ${boothNo || ''}`,
            searchPlaceholder: "નામ અથવા વોટર ID થી શોધો...",
            noData: "આ બૂથ માટે કોઈ ઇન્સ્ટાગ્રામ યુઝર્સ મળ્યા નથી.",
            loadingData: "યુઝર્સ લાવી રહ્યા છીએ...",
            idCardNo: "ID Card No",
            age: "Age",
        }
    };

    const txt = t[lang] || t.gu;

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!appId || !boothNo) {
            navigate(-1);
            return;
        }
        fetchInstagramUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, boothNo]);

    const fetchInstagramUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_insta_users`, {
                app_id: appId,
                booth_no: boothNo
            });

            if (response.data && response.data.success) {
                setUsers(response.data.data.result || []);
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error("Error fetching instagram users:", err);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter Logic
    const filteredUsers = users.filter(user => {
        const fullName = `${user.eng_f_name} ${user.eng_m_name} ${user.eng_surname}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) ||
            user.idcard_no?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div className="insta-users-page-container">
            {/* Header */}
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">

                {/* Search Bar */}
                <div className="search-bar-container mb-4 slide-up-element">
                    <div className="search-wrapper">
                        <svg className="search-icon" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={txt.searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5 slide-up-element">
                        <svg className="spinner-svg" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                        </svg>
                        <p className="mt-3 text-muted font-weight-bold">{txt.loadingData}</p>
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="text-center text-muted p-5 bg-white rounded shadow-sm border slide-up-element">
                        <div style={{ fontSize: '50px', marginBottom: '16px' }}>📷</div>
                        <h4>{txt.noData}</h4>
                    </div>
                ) : (
                    <div className="users-grid row slide-up-element" style={{ animationDelay: '0.1s' }}>
                        {filteredUsers.map((user, index) => (
                            <div key={user.id || index} className="col-12 col-sm-6 col-md-4 col-lg-4 mb-4">
                                <div className="user-card-horizontal">

                                    {/* 1. Left Avatar (Rounded Square, Pink for F, Blue for M) */}
                                    <div className={`user-avatar-box ${user.sex === 'F' ? 'bg-pink' : 'bg-blue'}`}>
                                        {user.sex === 'F' ? (
                                            // Female Silhouette Icon
                                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                                <circle cx="12" cy="7" r="3" />
                                                <path d="M16 11H8c-1.1 0-2 .9-2 2v3h3v6h2v-6h2v6h2v-6h3v-3c0-1.1-.9-2-2-2z" />
                                            </svg>
                                        ) : (
                                            // Male Silhouette Icon
                                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                                <circle cx="12" cy="7" r="3" />
                                                <path d="M16 11H8c-1.1 0-2 .9-2 2v5h3v4h2v-4h3v-5c0-1.1-.9-2-2-2z" />
                                            </svg>
                                        )}
                                    </div>

                                    {/* 2. Center Info */}
                                    <div className="user-info-body">
                                        <h3 className="user-name-title">
                                            {user.eng_f_name} {user.eng_m_name} {user.eng_surname}
                                        </h3>

                                        <div className="user-meta-line">
                                            <span className="meta-label">{txt.idCardNo}:</span>
                                            <span className="meta-value">{user.idcard_no || '-'}</span>
                                        </div>

                                        <div className="user-meta-line">
                                            <span className="meta-label">{txt.age}:</span>
                                            <span className="meta-value">{user.age || '-'}</span>
                                        </div>

                                        <div className="user-address">
                                            {user.eng_localityid || '-'}
                                        </div>
                                    </div>

                                    {/* 3. Right Action (Instagram Button) */}
                                    <div className="user-action-box">
                                        {user.insta && user.insta !== '-' ? (
                                            <a
                                                href={user.insta.startsWith('http') ? user.insta : `https://instagram.com/${user.insta.replace('@', '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="insta-circle-btn"
                                                title="View Instagram Profile"
                                            >
                                                <svg width="36" height="36" fill="#e1306c" viewBox="0 0 24 24">
                                                    <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
                                                </svg>
                                            </a>
                                        ) : (
                                            <div className="insta-circle-btn disabled" title="No Instagram Link">
                                                <svg width="36" height="36" fill="#cbd5e1" viewBox="0 0 24 24">
                                                    <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style jsx>{`
                .insta-users-page-container { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 40px; }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                /* Search Bar */
                .search-bar-container { display: flex; justify-content: flex-end; }
                .search-wrapper { position: relative; width: 100%; max-width: 400px; }
                .search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
                .search-input { width: 100%; padding: 12px 40px; border: 1px solid #cbd5e1; border-radius: 12px; font-size: 15px; color: #1e293b; background: white; outline: none; transition: 0.2s; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
                .clear-search-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: #f1f5f9; border: none; border-radius: 50%; padding: 4px; cursor: pointer; color: #64748b; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
                .clear-search-btn:hover { background: #e2e8f0; color: #1e293b; }

                /* Spinner */
                .spinner-svg { color: #3b82f6; animation: spin 1s linear infinite; margin: 0 auto; display: block; }
                @keyframes spin { 100% { transform: rotate(360deg); } }

                /* Users Grid - Bootstrap Grid System */
                .users-grid {
                    margin: 0 -12px;
                }

                /* Horizontal User Card */
                .user-card-horizontal {
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 20px 16px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.03);
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    height: 100%;
                    border: 1px solid #e2e8f0;
                }
                .user-card-horizontal:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px -8px rgba(0,0,0,0.12);
                }

                @media (max-width: 768px) {
                    .user-card-horizontal {
                        padding: 16px 12px;
                        gap: 12px;
                    }
                }

                /* Avatar Box */
                .user-avatar-box {
                    width: 52px;
                    height: 52px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .bg-pink { background: #fdf2f8; color: #db2777; }
                .bg-blue { background: #f0f8ff; color: #2563eb; }

                /* Center Info */
                .user-info-body {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    min-width: 0;
                }
                .user-name-title {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 900;
                    color: #1e293b;
                    line-height: 1.2;
                    word-break: break-word;
                }
                .user-meta-line {
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    flex-wrap: wrap;
                }
                .meta-label {
                    color: #64748b;
                    font-weight: 600;
                }
                .meta-value {
                    color: #1e293b;
                    font-weight: 800;
                }
                
                .user-address {
                    font-size: 11px;
                    color: #475569;
                    line-height: 1.4;
                    margin-top: 2px;
                    word-break: break-word;
                }

                /* Right Action (Instagram Circle Button) */
                .user-action-box {
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .insta-circle-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    text-decoration: none;
                    transition: transform 0.2s;
                }
                .insta-circle-btn:not(.disabled):hover {
                    transform: scale(1.1);
                }
                .insta-circle-btn.disabled {
                    cursor: not-allowed;
                }

                /* Responsive Grid Breakpoints */
                @media (min-width: 1200px) {
                    .users-grid .col-lg-4 {
                        flex: 0 0 33.333%;
                        max-width: 33.333%;
                    }
                }
                
                @media (max-width: 992px) {
                    .users-grid .col-md-4 {
                        flex: 0 0 33.333%;
                        max-width: 33.333%;
                    }
                    .user-name-title {
                        font-size: 14px;
                    }
                }
                
                @media (max-width: 768px) {
                    .users-grid .col-sm-6 {
                        flex: 0 0 50%;
                        max-width: 50%;
                    }
                    .user-avatar-box {
                        width: 45px;
                        height: 45px;
                    }
                    .user-avatar-box svg {
                        width: 20px;
                        height: 20px;
                    }
                }
                
                @media (max-width: 576px) {
                    .users-grid .col-12 {   
                        flex: 0 0 100%;
                        max-width: 100%;
                    }
                    .user-card-horizontal {
                        padding: 16px;
                    }
                    .user-avatar-box {
                        width: 48px;
                        height: 48px;
                    }
                }

                /* Animation */
                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default BoothWiseInstagramUser;